import { SHAPE_DATA } from '@components/previewUI/utils/constants.js';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { sortItems } from '@utils/functions.js';

const initialState = {
  isLoading: false,
  isActive: false,
  id: 0,
  uid: 0,
  sid: 0,

  blockContent: {
    items: [],
    groups: [],
    priceUnits: [],
    heading: {
      title: '',
      description: '',
      textAlign: 'left',
      image: '',
      divider: '',
      position: '',
      label: '',
      labelColor: '#000000',
      labelBackgroundColor: '#4bc6d6',
      labelFontSize: 12,
      labelAlign: 'center',
      labelShape: SHAPE_DATA.none,
      labelIsGradient: false,
      labelGradientColor: 'linear-gradient(0deg, rgba(169, 46, 163, 1) 0%, rgba(255, 217, 92, 1) 100%)',
      labelUrl: '',
    },
  },
  blockDesign: {},
  itemDesign: {},
  buttonDesign: {},
  containerDesign: {},
};

export const pricingSlice = createSlice({
  name: 'pricing',
  initialState,
  reducers: {
    pricingStore: (state, action) => {
      const { id, isActive, sid, uid, data } = action.payload;

      const block = data ?? {};
      const { blockContent, blockDesign } = block;
      const { itemDesign, containerDesign, buttonDesign } = blockDesign;

      state.isLoading = false;
      state.id = id ?? 0;
      state.isActive = isActive;
      state.sid = sid;
      state.uid = uid;

      state.itemDesign = {
        textColor: itemDesign.textColor ?? '#000000',
        backgroundColor: itemDesign.backgroundColor ?? '#dddddd',
        shape: itemDesign.shape ?? 'round',
        border: itemDesign.border ?? 'none',
        borderColor: itemDesign.borderColor ?? '#ECECF0',
        shadow: itemDesign.shadow ?? 'none',
        shadowColor: itemDesign.shadowColor ?? '#000000',
        layout: itemDesign.layout ?? 'none',
        opacity: itemDesign.opacity ?? 100,
      };

      state.buttonDesign = {
        textColor: buttonDesign.textColor ?? '#000000',
        backgroundColor: buttonDesign.backgroundColor ?? '#4bc6d6',
        border: buttonDesign.border ?? 'none',
        borderColor: buttonDesign.borderColor ?? 'none',
        gradientColor1: buttonDesign.gradientColor1 ?? 'none',
        gradientColor2: buttonDesign.gradientColor2 ?? 'none',
        gradientDirection: buttonDesign.gradientDirection ?? 'none',
        gradientOffset: buttonDesign.gradientOffset ?? 'none',
        isGradient: buttonDesign.isGradient ?? 'none',
        opacity: buttonDesign.opacity ?? 100,
        shadow: buttonDesign.shadow ?? 'none',
        shadowColor: buttonDesign.shadowColor ?? 'none',
        shape: buttonDesign.shape ?? 'none',
        text: buttonDesign.text ?? 'none',
        textAlign: buttonDesign.textAlign ?? 'none',
      };

      state.containerDesign = containerDesign ?? {};

      state.blockContent = {
        items: blockContent.items ?? [],
        groups: blockContent.groups ?? [],
        priceUnits: blockContent.priceUnits ?? [],
        heading: {
          ...blockContent.heading,
          title: blockContent.heading.title ?? '',
          description: blockContent.heading.description ?? '',
          textAlign: blockContent.heading.textAlign ?? 'left',
          image: blockContent.heading.image ?? '',
          divider: blockContent.heading.divider ?? '',
          position: blockContent.heading.position ?? '',
          label: blockContent.heading.label ?? '',
          labelColor: blockContent.heading.labelColor ?? '#000000',
          labelBackgroundColor: blockContent.heading.labelBackgroundColor ?? '#ffffff',
          labelFontSize: blockContent.heading.labelFontSize ?? 12,
          labelAlign: blockContent.heading.labelAlign ?? 'center',
          labelShape: blockContent.heading.labelShape ?? SHAPE_DATA.none,
          labelIsGradient: blockContent.heading.labelIsGradient ?? false,
          labelGradientColor: blockContent.heading.labelGradientColor ?? 'linear-gradient(0deg, rgba(169, 46, 163, 1) 0%, rgba(255, 217, 92, 1) 100%)',
          labelUrl: blockContent.heading.labelUrl ?? '',
        },
      };
    },
    pricingClear: () => initialState,

    pricingUpdateIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    pricingUpdateIsActive: (state, action) => {
      state.isActive = action.payload;
    },
    pricingUpdateBlockContent: (state, action) => {
      state.blockContent = action.payload;
    },
    pricingUpdateBlockDesign: (state, action) => {
      state.blockDesign = action.payload;
    },
  },
});

export const { pricingStore, pricingClear, pricingUpdateIsLoading, pricingUpdateIsActive, pricingUpdateBlockContent, pricingUpdateBlockDesign } = pricingSlice.actions;

export const selectPricingIsLoading = (state) => state.pricing.isLoading;

export const selectPricingId = (state) => state.pricing.id;
export const selectPricingUID = (state) => state.pricing.uid;
export const selectPricingSID = (state) => state.pricing.sid;

export const selectPricingBlockContent = (state) => state.pricing.blockContent;

export const selectLabelBackgroundColor = createSelector(
  (state) => state.pricing.blockContent.items,
  (items) => items.find((item) => item.labelBackgroundColor !== null)?.labelBackgroundColor ?? null,
);

export const selectPricingSortItems = createSelector([selectPricingBlockContent], ({ items }) => {
  return sortItems(items);
});
export const selectPricingItemDesign = (state) => state.pricing.itemDesign;
export const selectPricingButtonDesign = (state) => state.pricing.buttonDesign;
export const selectPricingContainerDesign = (state) => state.pricing.containerDesign;
export const selectPricingBlockDesign = (state) => state.pricing.blockDesign;
export const selectPricingGroups = (state) => state.pricing.blockContent.groups;

export default pricingSlice.reducer;
